import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ASSETS } from "@/lib/site";
import SkinWeatherStrip from "@/components/SkinWeatherStrip";
import HeroShowcase from "@/components/HeroShowcase";

const lineParent = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.3 } },
};
const lineChild = {
  hidden: { y: "110%" },
  show: { y: "0%", transition: { duration: 1.1, ease: [0.16, 1, 0.3, 1] } },
};

function MaskLine({ children }) {
  return (
    <span className="block overflow-hidden">
      <motion.span variants={lineChild} className="block">
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const glowScale = useTransform(scrollYProgress, [0, 1], [1, 1.25]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen pt-[68px] overflow-hidden"
    >
      <SkinWeatherStrip />
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 grid lg:grid-cols-[0.95fr_1.05fr] gap-8 items-center min-h-[calc(100vh-68px)]">
        {/* Left — kinetic type */}
        <div className="relative z-10 pt-10 lg:pt-0">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="overline mb-6"
          >
            JAKS Pharma · Glutathione Science
          </motion.p>

          <motion.h1
            variants={lineParent}
            initial="hidden"
            animate="show"
            className="font-serif font-light tracking-tighter leading-[0.92] text-[color:var(--relumia-ink)] text-5xl sm:text-6xl lg:text-7xl xl:text-[5.4rem]"
          >
            <MaskLine>The pinnacle of</MaskLine>
            <MaskLine>
              <span className="italic text-gold">radiant</span> skin,
            </MaskLine>
            <MaskLine>from within.</MaskLine>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.3, duration: 0.9 }}
            className="mt-8 max-w-md text-[15px] leading-relaxed text-[color:var(--relumia-muted)]"
          >
            Relumia is a celebrated glutathione brand — an advanced formula of
            L-Glutathione, Vitamin C, D3, Zinc &amp; Curcumin to support brighter,
            even-toned and youthful skin.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.5, duration: 0.9 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#glutathione"
              data-testid="hero-cta-primary"
              className="group inline-flex items-center gap-3 rounded-full bg-[color:var(--relumia-ink)] px-7 py-3.5 text-[13px] tracking-wide text-white hover:bg-[color:var(--relumia-gold-deep)] transition-colors"
            >
              Discover the science
              <span className="transition-transform group-hover:translate-x-1">→</span>
            </a>
            <a
              href="#contact"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center rounded-full border border-[color:var(--relumia-ink)]/20 px-7 py-3.5 text-[13px] tracking-wide text-[color:var(--relumia-ink)] hover:border-[color:var(--relumia-gold)] transition-colors"
            >
              Enquire now
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.8, duration: 1 }}
            className="mt-14 flex items-center gap-8 text-[color:var(--relumia-muted)]"
          >
            {[
              ["30", "Tablets · 3×10"],
              ["5", "Active actives"],
              ["100%", "Dermatologist-formulated"],
            ].map(([n, l]) => (
              <div key={l}>
                <div className="font-serif text-3xl text-[color:var(--relumia-ink)]">{n}</div>
                <div className="text-[11px] tracking-wide mt-1">{l}</div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Right — Relumia brand film (auto-play, loop, muted) */}
        <HeroShowcase />
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-[color:var(--relumia-muted)]"
      >
        <span className="text-[10px] tracking-[0.3em] uppercase">Scroll</span>
        <span className="h-8 w-px bg-[color:var(--relumia-champagne)]" />
      </motion.div>
    </section>
  );
}
